// Inverted UV-sphere for equirectangular panoramas.
//
// Why this approach:
// • pc.createSphere produces the standard latitude/longitude tessellation with
//   correct UVs that align with PlayCanvas's asset-loaded textures (flipY).
// • Rendering the back face (CULLFACE_FRONT) puts the camera *inside* the
//   sphere with zero overhead.
// • emissiveMap-only material means the sphere ignores all scene lighting.
// • U is flipped via material tiling so text reads right-side-out (otherwise
//   back-face render mirrors it).
// • Sphere rotated -90° around Y so the equirect's u=0.5 "front" aligns with
//   the camera's -Z forward direction.
// This matches the production Paçoca demo (apps/web/public/playcanvas-pano-demo.html).

import * as pc from 'playcanvas';

const DEFAULT_RADIUS = 100;
const DEFAULT_LON_BANDS = 256;
const DEFAULT_LAT_BANDS = 128;

export interface PanoramaSphereOptions {
  radius?: number;
  longitudeBands?: number;
  latitudeBands?: number;
  /** Render layer ids. Defaults to PlayCanvas's default layer set if omitted. */
  layers?: number[];
}

export class PanoramaSphere {
  readonly entity: pc.Entity;
  private readonly material: pc.StandardMaterial;
  private currentTexture: pc.Texture | null = null;

  constructor(app: pc.Application, opts: PanoramaSphereOptions = {}) {
    const radius = opts.radius ?? DEFAULT_RADIUS;
    const lon = opts.longitudeBands ?? DEFAULT_LON_BANDS;
    const lat = opts.latitudeBands ?? DEFAULT_LAT_BANDS;

    const mesh = pc.createSphere(app.graphicsDevice, {
      radius,
      latitudeBands: lat,
      longitudeBands: lon,
    });

    this.material = new pc.StandardMaterial();
    this.material.useLighting = false;
    this.material.useFog = false;
    this.material.useTonemap = false;
    this.material.useSkybox = false;
    this.material.diffuse = new pc.Color(0, 0, 0);
    this.material.emissive = new pc.Color(1, 1, 1);
    this.material.cull = pc.CULLFACE_FRONT;
    // U-flip — back-face render mirrors the sphere; without this, text reads backwards.
    this.material.emissiveMapTiling = new pc.Vec2(-1, 1);
    this.material.emissiveMapOffset = new pc.Vec2(1, 0);
    this.material.update();

    const meshInstance = new pc.MeshInstance(mesh, this.material);
    meshInstance.castShadow = false;
    meshInstance.receiveShadow = false;

    this.entity = new pc.Entity('panorama-sphere');
    this.entity.addComponent('render', {
      meshInstances: [meshInstance],
      ...(opts.layers ? { layers: opts.layers } : {}),
    });
    this.entity.setLocalPosition(0, 0, 0);
    // Rotate so equirect u=0.5 (panorama "front") faces camera's -Z forward.
    this.entity.setLocalEulerAngles(0, -90, 0);
    app.root.addChild(this.entity);
  }

  setTexture(texture: pc.Texture | null): void {
    if (texture === this.currentTexture) return;
    this.currentTexture = texture;
    this.material.emissiveMap = texture;
    this.material.update();
  }

  /**
   * Rotate the panorama in yaw without touching the entity or the camera.
   *
   * Done as a horizontal UV shift rather than an entity rotation for two
   * reasons: it is arithmetically the same operation as rolling the equirect's
   * pixels (so a value measured on the image file transfers unchanged), and it
   * leaves the entity transform free for anything else that wants it.
   *
   * The base U mapping is `1 - u` (emissiveMapTiling.x = -1, offset.x = 1) —
   * the flip that undoes the back-face mirror. Adding d to the offset makes it
   * `1 + d - u`, sampling further along the image, which slides the content
   * LEFT. So positive degrees move content left, matching the pixel-roll sign.
   *
   * Wrapping is safe: assetLoader sets addressU = ADDRESS_REPEAT on every
   * panorama texture, so the shifted seam re-enters from the other edge
   * instead of smearing the last column.
   */
  setYawOffset(degrees: number): void {
    const deg = Number.isFinite(degrees) ? degrees : 0;
    this.material.emissiveMapOffset = new pc.Vec2(1 + deg / 360, 0);
    this.material.update();
  }

  getCurrentTexture(): pc.Texture | null {
    return this.currentTexture;
  }

  /**
   * Apply a per-channel tint to the panorama by mutating the emissive colour.
   * 1,1,1 is neutral; warmer / cooler / dimmer biases shift the room's mood
   * without needing to swap the underlying panorama. Used by the material
   * panel as the cheap Sprint-1 "PBR live swap" stand-in.
   */
  setEmissiveTint(r: number, g: number, b: number): void {
    this.material.emissive.set(
      Math.max(0, r),
      Math.max(0, g),
      Math.max(0, b),
    );
    this.material.update();
  }

  /** Restore the default neutral 1,1,1 tint. */
  resetTint(): void {
    this.setEmissiveTint(1, 1, 1);
  }

  destroy(): void {
    this.entity.destroy();
    this.material.destroy();
  }
}
